import React, { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FaLock, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { selectActiveLocation } from "../common/slices/location";
import { createUser, updateUser, createRateOverride, updateRateOverride, selectUser } from "../common/slices/user";
import capitalizeFirstLetter from "../utils/capitalizeFirstLetter";
import { BlackButton, ButtonBase, LightButton } from "./Buttons";
import Divider from "./Divider";
import FormEmailInput from "./FormEmailInput";
import FormPhoneNumber from "./FormPhoneNumber";
import FormSelectCrewMember from "./FormSelectCrewMember";
import FormSelectGroup from "./FormSelectGroup";
import FormSelectLocation from "./FormSelectLocation";
import FormSelectRole from "./FormSelectRole";
import FormTextInput from "./FormTextInput";
import Modal from "./Modal";
import Row from "./Row";
import { H1, P, Pmb } from "./Typography";
import { fetchCrewMember } from "../common/slices/crewMember";
import ModalSetPassword from "./ModalSetPassword";
import { useState } from "react";
import { ErrorContext } from "../ErrorContext";
import { assignUserToGroups } from "../common/slices/group";

const marshall = (input, location) => ({
    ...input,
    groups: input?.groups?.map(g => ({ value: g.id, label: g.name })),
    divisions: input?.divisions?.map(g => ({ value: g.id, label: g.name })),
    crewMemberId: { value: input?.crewMember?.id, label: input?.crewMember?.name },
    locationId: { value: input?.location?.id || location?.id, label: input?.location?.name || location?.name },
    role: { value: input?.role || 'WORKER', label: capitalizeFirstLetter(input?.role) || 'Worker' }
})

const unmarshall = (input) => ({
    groups: [],
    divisions: [],
    ...input,
    role: input.role?.value,
    crewMemberId: input.crewMemberId?.value,
    locationId: input.locationId?.value,
})

const ModalEditUser = ({ user, onClose }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const location = useSelector(selectActiveLocation);
    const formValues = marshall(user, location);
    const [crewMember, setCrewMember] = useState(null);
    const currentUser = useSelector(selectUser);


    const {
        control,
        formState: { errors, isSubmitting, isValid },
        watch,
        setValue,
        getValues,
        handleSubmit
    } = useForm({
        values: { ...formValues },
    })

    const locationId = watch("locationId")
    const crewMemberId = watch("crewMemberId")
    const formProps = { control, errors, locationId: locationId?.value }

    const onSave = async (data) => {
        try {
            const input = unmarshall(data);
            if (!data.id) user = await dispatch(createUser(input));
            else user = await dispatch(updateUser(input));
            if (data.rateOverride && !crewMember.rateOverride) {
                await dispatch(createRateOverride({
                    crewMemberId: input.crewMemberId,
                    locationId: input.locationId,
                    rate: data.rateOverride
                }))
            } else if (data.rateOverride && crewMember.rateOverride) {
                await dispatch(updateRateOverride({
                    crewMemberId: input.crewMemberId,
                    locationId: input.locationId,
                    rate: data.rateOverride
                }))
            }
            await dispatch(assignUserToGroups(input.crewMemberId, input.locationId, input.divisions?.map(g => g.value), true))
            await dispatch(assignUserToGroups(input.crewMemberId, input.locationId, input.groups?.map(g => g.value), false))
            onClose();
            toast.success("Saved successfully");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to save user", forSupport: ex.message })
        }
    }

    useEffect(() => {
        (async () => {
            try {
                if (crewMemberId?.value && locationId?.value) {
                    const cm = await dispatch(fetchCrewMember(crewMemberId.value, locationId.value))
                    if (cm) {
                        setCrewMember(cm);
                        const values = getValues();
                        const [firstName, lastName] = cm?.name?.split(' ');

                        if (!values.id || !values.firstName) {
                            setValue('firstName', firstName);
                        }

                        if (!values.id || !values.lastName) {
                            setValue('lastName', lastName);
                        }

                        setValue('rate', cm?.rate);
                        setValue('rateOverride', cm?.rateOverride?.rate || '');

                        if (!values.id || !values.email) {
                            setValue('email', cm?.email);
                        }
                    }
                }
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to get crew member", forSupport: ex.message })
            }
        })();
    }, [crewMemberId.value, locationId.value])

    return (
        <Modal show size="70%" zindex='1200'>
            <Row>
                <H1>{user?.id ? "Edit" : "Create"} User</H1>
                <ButtonBase onClick={onClose} style={{ marginLeft: 'auto' }}><FaTimes size='16px' color="#000" /></ButtonBase>
            </Row>

            <Pmb>App Access Information</Pmb>
            <P>Select the level of access that this user should have on the United Roofing app.</P>
            <Row>
                <FormSelectRole {...formProps} name="role" label="Role" placeholder="Select a role" rules={{ required: true }} />
                {currentUser.hasDivisionAccess && <FormSelectGroup {...formProps} name="divisions" label="Divisions" placeholder="Select user division(s)" isMulti containerStyle={{ flex: 2 }} onlyDivisions />}
                <FormSelectGroup {...formProps} name="groups" label="Groups" placeholder="Select user groups" isMulti containerStyle={{ flex: 2 }} />
            </Row>

            <Divider.Horizontal />

            <Pmb>Quick Books Sync Information</Pmb>
            <P>Select the appropriate United Roofing location and the synchronized Quick Books user to associate with this profile.</P>
            <Row>
                <FormSelectLocation {...formProps} name="locationId" label="Location" placeholder="Select location" rules={{ required: true }} />
                <FormSelectCrewMember {...formProps} disabled={!locationId?.value} name="crewMemberId" label="Associated QB User" placeholder="Select user" cacheOptions={locationId?.value} rules={{ required: true }} />
            </Row>
            <Row>
                <FormTextInput {...formProps} name="rate" label="Quick Books Rate ($)" placeholder="Rate supplied from QBs" disabled={true} />
                <FormTextInput {...formProps} name="rateOverride" label="Rate Override ($)" placeholder="Enter a rate to override calculations" rules={{ pattern: /^(([0-9]+([.][0-9]*)?|[.][0-9]+)?)$/i }} />
            </Row>

            <Divider.Horizontal />

            <Pmb>User Details</Pmb>
            <P>Use the form below to enter basic user information for this user profile. The email address you enter will become the users username to access the system.</P>
            <Row>
                <FormTextInput {...formProps} name="firstName" label="First Name" placeholder="Enter first name" rules={{ required: true }} />
                <FormTextInput {...formProps} name="lastName" label="Last Name" placeholder="Enter last name" rules={{ required: true }} />
            </Row>
            <Row>
                <FormEmailInput {...formProps} name="email" label="Email" placeholder="Enter email address" rules={{ required: true }} disabled={user?.email} />
                <FormPhoneNumber {...formProps} name="phoneNumber" label="Phone Number" placeholder="Enter phone number" />
            </Row>

            <Divider.Horizontal />
            <Row>
                <BlackButton onClick={handleSubmit(onSave)} disabled={isSubmitting || !isValid}>Save</BlackButton>
                <LightButton onClick={onClose} disabled={isSubmitting}>Cancel</LightButton>

                <div style={{ flex: 1 }} />
                <LightButton onClick={() => setShowPasswordModal(true)} disabled={isSubmitting || !formValues.id}>Reset Password <FaLock /></LightButton>
            </Row>
            {showPasswordModal && <ModalSetPassword user={user} onClose={() => setShowPasswordModal(false)} zindex={9999} />}
        </Modal>
    );
};

export default ModalEditUser;
